"use client";

import React from "react";
import { Mail, MapPin, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: MapPin,
  title: "Our Headquarters",
  detail: "Quitman, GA"
}, {
  icon: Phone,
  title: "Direct Line",
  detail: "229-329-1016"
}, {
  icon: Mail,
  title: "Email Us",
  detail: "sales@rpg-studios.com"
}];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="187782" className="p-8 bg-white/5 backdrop-blur-xl border border-[#00f2ff]/30 rounded-none text-center animate-in fade-in duration-500">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00f2ff]/20 mb-6">
          <Mail className="w-8 h-8 text-[#00f2ff]" />
        </div>
        <h3 className="text-2xl font-serif text-[#00f2ff] mb-4">Inquiry Received</h3>
        <p className="text-slate-300">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <FormField control={control} name="name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-400 font-mono text-xs uppercase tracking-widest">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="bg-white/5 border-b-2 border-white/10 focus:border-[#00f2ff] text-white placeholder:text-slate-600 focus-visible:ring-0 rounded-none h-12 transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-400 font-mono text-xs uppercase tracking-widest">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@example.com" className="bg-white/5 border-b-2 border-white/10 focus:border-[#00f2ff] text-white placeholder:text-slate-600 focus-visible:ring-0 rounded-none h-12 transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-400 font-mono text-xs uppercase tracking-widest">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="229-329-1016" className="bg-white/5 border-b-2 border-white/10 focus:border-[#00f2ff] text-white placeholder:text-slate-600 focus-visible:ring-0 rounded-none h-12 transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Please enter your message"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-slate-400 font-mono text-xs uppercase tracking-widest">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="How can we help your business grow?" className="bg-white/5 border-b-2 border-white/10 focus:border-[#00f2ff] text-white placeholder:text-slate-600 focus-visible:ring-0 rounded-none min-h-[120px] resize-none transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage className="text-red-400" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-950/50 border border-red-500/50 rounded-none">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-none bg-gradient-to-r from-[#00f2ff] to-[#008c9e] hover:from-[#00d8e4] hover:to-[#007a8a] text-black font-bold text-lg transition-all duration-300 border-none shadow-lg shadow-[#00f2ff]/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 w-5 h-5" />
            Initializing...
          </> : "Start Partnership"}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contactform" className="bg-background text-foreground">
      <div className="bg-[#0a051a] py-[100px] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px]" />
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#00f2ff]/10 blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[#008c9e]/10 blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            
            <div ref={leftColRef} className={`transition-all duration-1000 ease-out ${leftColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="mb-12">
                <h2 className="font-serif text-[#00f2ff] text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Building Relationships, Not Just Businesses
                </h2>
                <p className="text-slate-300 text-lg md:text-xl font-sans leading-relaxed max-w-lg">
                  Ready to increase your online presence and confidence? Reach out to RPG-Studios for expert WordPress design, SEO, and strategic digital growth.
                </p>
              </div>

              <div className="space-y-6 mb-12">
                {contactDetails.map((item, i) => <Card key={i} data-index={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-none overflow-hidden hover:bg-white/10 transition-colors duration-300 group">
                    <CardContent className="p-6 flex items-center gap-6">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-[#00f2ff]/10 border border-[#00f2ff]/20 rounded-none group-hover:border-[#00f2ff]/50 transition-colors">
                        <item.icon className="w-6 h-6 text-[#00f2ff]" />
                      </div>
                      <div>
                        <h4 className="text-slate-400 text-sm font-mono font-medium mb-1 uppercase tracking-widest">
                          {item.title}
                        </h4>
                        <p className="text-slate-100 text-lg font-medium">
                          {item.detail}
                        </p>
                      </div>
                    </CardContent>
                  </Card>)}
              </div>
            </div>

            <div ref={rightColRef} className={`transition-all duration-1000 delay-200 ease-out ${rightColVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-none p-8 md:p-10 shadow-2xl relative">
                <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-[#00f2ff]/30 pointer-events-none" />
                <h3 className="text-2xl font-serif text-white mb-8 border-b border-white/10 pb-4">
                  Send a Message
                </h3>
                
                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="contact" defaultValues={{
                name: "",
                email: "",
                phone: "",
                message: ""
              }} submitText="Start Partnership" successMessage="Thank you for reaching out! We will get back to you shortly to discuss your digital growth strategy.">
                  <ContactFormContent />
                </FormProvider>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}